import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { cn } from '../lib/utils';
import { hoyLocal, toYMD } from '../utils/fechaLocal';

interface CustomDiscountForm {
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  discount_value_services: number; // solo cuando target=both con split
  min_amount: number;
  max_discount: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category' | 'filter';
  audience_type: 'all' | 'specific_clients';
  target_type: 'products' | 'services' | 'both';
  target_id: string;
  target_name: string;
  active_days: string[];
  active_hours: string[];
  start_date: string;
  end_date: string;
  usage_type: 'single' | 'multiple';
  reason: 'fidelizacion' | 'reclamo' | 'promo' | 'cierre_venta' | 'error_interno' | '';
  eligible_products: string[];
  eligible_services: string[];
  // "Todos los servicios menos estos" (por nombre)
  excluded_services: string[];
  eligible_clients: string[];
  max_uses: number;
  priority: number;
  filter_brand: string;
  filter_model: string;
  filter_category: string;
  filter_supplier_name: string;
  filter_measure_width: string;
  filter_measure_ratio: string;
  filter_rim: string;
}

interface DistinctMeta {
  brands: string[];
  modelsByBrand: Record<string, string[]>;
  measureWidths: number[];
  measureRatios: number[];
  rims: number[];
  suppliers: string[];
  categories: string[];
}

const EMPTY_META: DistinctMeta = {
  brands: [],
  modelsByBrand: {},
  measureWidths: [],
  measureRatios: [],
  rims: [],
  suppliers: [],
  categories: []
};

interface DiscountProduct {
  id: string;
  name: string;
  isService?: boolean;
}

interface DiscountClient {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  type?: 'individual' | 'company';
}

const DatePicker = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);
  const dateValue = value ? new Date(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground"
          )}
        >
          {dateValue ? dateValue.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Seleccionar fecha'}
          <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          captionLayout="dropdown"
          fromYear={1900}
          toYear={new Date().getFullYear() + 10}
          onSelect={(date: Date | undefined) => {
            onChange(date ? toYMD(date) : '');
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

const CreateDiscount: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  // Audience: a quien aplica el descuento (todos o clientes especificos)
  const [audienceType, setAudienceType] = useState<'all' | 'specific_clients'>('all');
  // Target: a que items se aplica (productos, servicios o ambos)
  const [targetType, setTargetType] = useState<'products' | 'services' | 'both'>('both');
  // Sub-modos dentro de productos / servicios
  const [productMode, setProductMode] = useState<'filters' | 'specific'>('filters');
  const [serviceMode, setServiceMode] = useState<'all' | 'specific'>('all');
  // Cuando target=both, permitir % distinto para productos y servicios
  const [splitDiscount, setSplitDiscount] = useState<boolean>(false);
  const [productsList, setProductsList] = useState<DiscountProduct[]>([]);
  const [clientsList, setClientsList] = useState<DiscountClient[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<DistinctMeta>(EMPTY_META);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<CustomDiscountForm>({
    defaultValues: {
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      discount_value_services: 0,
      min_amount: 0,
      max_discount: 0,
      applies_to: 'all',
      audience_type: 'all',
      target_type: 'both',
      target_id: '',
      target_name: '',
      active_days: [],
      active_hours: [],
      start_date: hoyLocal(),
      end_date: toYMD(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
      usage_type: 'multiple',
      reason: '',
      eligible_products: [],
      eligible_services: [],
      excluded_services: [],
      eligible_clients: [],
      max_uses: 0,
      priority: 0,
      filter_brand: '',
      filter_model: '',
      filter_category: '',
      filter_supplier_name: '',
      filter_measure_width: '',
      filter_measure_ratio: '',
      filter_rim: ''
    }
  });

  useEffect(() => {
    fetchSources();
    if (isEditing) fetchDiscount();
  }, []);

  const fetchSources = async () => {
    try {
      const token = localStorage.getItem('token');
      const [productsResponse, clientsResponse, metaResponse] = await Promise.all([
        axios.get('/api/products?limit=all', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/clients?limit=10000', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/products/distinct-meta', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const productsData = Array.isArray(productsResponse.data?.products)
        ? productsResponse.data.products
        : Array.isArray(productsResponse.data) ? productsResponse.data : [];
      const clientsData = Array.isArray(clientsResponse.data?.clients)
        ? clientsResponse.data.clients
        : Array.isArray(clientsResponse.data) ? clientsResponse.data : [];
      setProductsList(productsData);
      setClientsList(clientsData);
      setMeta({
        brands: metaResponse.data?.brands || [],
        modelsByBrand: metaResponse.data?.modelsByBrand || {},
        measureWidths: metaResponse.data?.measureWidths || [],
        measureRatios: metaResponse.data?.measureRatios || [],
        rims: metaResponse.data?.rims || [],
        suppliers: metaResponse.data?.suppliers || [],
        categories: metaResponse.data?.categories || []
      });
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  const fetchDiscount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/custom-discounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = response.data;
      // Inicializar audience desde el descuento (con fallback legacy)
      const audience = d.audience_type === 'specific_clients' ? 'specific_clients'
        : (Array.isArray(d.eligible_clients) && d.eligible_clients.length > 0 ? 'specific_clients' : 'all');
      setAudienceType(audience);

      // Inicializar target desde el descuento (con fallback legacy)
      let target: 'products' | 'services' | 'both';
      if (d.target_type === 'products' || d.target_type === 'services' || d.target_type === 'both') {
        target = d.target_type;
      } else {
        // Legacy
        if (d.applies_to === 'products' || d.applies_to === 'filter' || d.applies_to === 'specific_product' || d.applies_to === 'specific_brand') {
          target = 'products';
        } else if (d.applies_to === 'services') {
          target = 'services';
        } else {
          target = 'both';
        }
      }
      setTargetType(target);

      // Sub-modo productos
      const hasFilters = !!(d.filter_brand || d.filter_model || d.filter_category || d.filter_supplier_name || d.filter_measure_width || d.filter_measure_ratio || d.filter_rim);
      const hasSpecificProducts = Array.isArray(d.eligible_products) && d.eligible_products.length > 0;
      setProductMode(hasFilters ? 'filters' : (hasSpecificProducts ? 'specific' : 'filters'));

      // Sub-modo servicios
      const hasSpecificServices = Array.isArray(d.eligible_services) && d.eligible_services.length > 0;
      setServiceMode(hasSpecificServices ? 'specific' : 'all');

      // Split %
      const hasSplit = d.discount_value_services != null && Number(d.discount_value_services) > 0;
      setSplitDiscount(hasSplit);

      setValue('name', d.name);
      setValue('description', d.description);
      setValue('discount_type', d.discount_type);
      setValue('discount_value', d.discount_value);
      setValue('min_amount', d.min_amount);
      setValue('max_discount', d.max_discount || 0);
      setValue('applies_to', d.applies_to);
      setValue('target_id', d.target_id || '');
      setValue('target_name', d.target_name || '');
      setValue('active_days', d.active_days || []);
      setValue('active_hours', d.active_hours || []);
      setValue('start_date', d.start_date);
      setValue('end_date', d.end_date);
      setValue('usage_type', d.usage_type || 'multiple');
      setValue('reason', d.reason || '');
      setValue('eligible_products', d.eligible_products || []);
      setValue('eligible_services', d.eligible_services || []);
      setValue('excluded_services', Array.isArray(d.excluded_services) ? d.excluded_services : []);
      setValue('eligible_clients', d.eligible_clients || []);
      setValue('max_uses', d.max_uses || 0);
      setValue('priority', d.priority);
      setValue('discount_value_services', d.discount_value_services || 0);
      setValue('audience_type', audience);
      setValue('target_type', target);
      // Filtros (snake_case en backend)
      setValue('filter_brand', d.filter_brand || '');
      setValue('filter_model', d.filter_model || '');
      setValue('filter_category', d.filter_category || '');
      setValue('filter_supplier_name', d.filter_supplier_name || '');
      setValue('filter_measure_width', d.filter_measure_width != null ? String(d.filter_measure_width) : '');
      setValue('filter_measure_ratio', d.filter_measure_ratio != null ? String(d.filter_measure_ratio) : '');
      setValue('filter_rim', d.filter_rim != null ? String(d.filter_rim) : '');
    } catch (error) {
      console.error('Error cargando descuento:', error);
      toast.error('Error cargando descuento');
      navigate('/discounts');
    }
  };

  const buildPayload = (data: CustomDiscountForm) => {
    const payload: any = {
      ...data,
      usage_type: watch('usage_type') || data.usage_type,
      reason: watch('reason') || data.reason,
      eligible_products: watch('eligible_products') || data.eligible_products,
      eligible_services: watch('eligible_services') || data.eligible_services,
      excluded_services: watch('excluded_services') || data.excluded_services || [],
      eligible_clients: watch('eligible_clients') || data.eligible_clients
    };

    const toIntOrNull = (v: string) => {
      const t = String(v || '').trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    };
    const toTextOrNull = (v: string) => {
      const t = String(v || '').trim();
      return t || null;
    };

    // Audience + target nuevos
    payload.audience_type = audienceType;
    payload.target_type = targetType;

    // Calcular applies_to legacy basado en target (para back-compat con codigo viejo)
    const productHasFilters = (
      (watch('filter_brand') || data.filter_brand) ||
      (watch('filter_model') || data.filter_model) ||
      (watch('filter_category') || data.filter_category) ||
      (watch('filter_supplier_name') || data.filter_supplier_name) ||
      (watch('filter_measure_width') || data.filter_measure_width) ||
      (watch('filter_measure_ratio') || data.filter_measure_ratio) ||
      (watch('filter_rim') || data.filter_rim)
    );
    if (targetType === 'products') {
      payload.applies_to = (productMode === 'filters' && productHasFilters) ? 'filter' : 'products';
    } else if (targetType === 'services') {
      payload.applies_to = 'services';
    } else {
      // both - usamos 'filter' si hay filtros de productos, sino 'all'
      payload.applies_to = productHasFilters ? 'filter' : 'all';
    }

    // Limpieza de listas segun el target seleccionado
    if (targetType === 'services') {
      payload.eligible_products = [];
    }
    if (targetType === 'products') {
      payload.eligible_services = [];
      payload.excluded_services = [];
    }
    if (audienceType === 'all') {
      payload.eligible_clients = [];
    }
    // Si el modo de productos es filtros, no enviamos lista especifica
    if ((targetType === 'products' || targetType === 'both') && productMode === 'filters') {
      payload.eligible_products = [];
    }
    // Si el modo de servicios es 'all' (todos), no enviamos lista especifica
    if ((targetType === 'services' || targetType === 'both') && serviceMode === 'all') {
      payload.eligible_services = [];
    }
    // Las exclusiones son de "todos los servicios"; con lista específica no hacen falta
    if (serviceMode === 'specific') {
      payload.excluded_services = [];
    }

    // Filtros estructurados de productos: solo se envian si target incluye productos y modo=filtros
    const includesProducts = targetType === 'products' || targetType === 'both';
    if (includesProducts && productMode === 'filters') {
      payload.filter_brand = toTextOrNull(watch('filter_brand') || data.filter_brand);
      payload.filter_model = toTextOrNull(watch('filter_model') || data.filter_model);
      payload.filter_category = toTextOrNull(watch('filter_category') || data.filter_category);
      payload.filter_supplier_name = toTextOrNull(watch('filter_supplier_name') || data.filter_supplier_name);
      // Las medidas solo aplican si la categoria es neumaticos
      const cat = String(watch('filter_category') || '').trim().toLowerCase();
      if (cat === 'neumaticos') {
        payload.filter_measure_width = toIntOrNull(watch('filter_measure_width') || data.filter_measure_width);
        payload.filter_measure_ratio = toIntOrNull(watch('filter_measure_ratio') || data.filter_measure_ratio);
        payload.filter_rim = toIntOrNull(watch('filter_rim') || data.filter_rim);
      } else {
        payload.filter_measure_width = null;
        payload.filter_measure_ratio = null;
        payload.filter_rim = null;
      }
    } else {
      payload.filter_brand = null;
      payload.filter_model = null;
      payload.filter_category = null;
      payload.filter_supplier_name = null;
      payload.filter_measure_width = null;
      payload.filter_measure_ratio = null;
      payload.filter_rim = null;
    }

    // discount_value_services: solo cuando target=both y splitDiscount activado
    if (targetType === 'both' && splitDiscount) {
      const v = Number(watch('discount_value_services') || data.discount_value_services || 0);
      payload.discount_value_services = Number.isFinite(v) && v >= 0 ? v : 0;
    } else {
      payload.discount_value_services = null;
    }

    return payload;
  };

  const onSubmit = async (data: CustomDiscountForm) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = buildPayload(data);
      if (isEditing) {
        await axios.put(`/api/custom-discounts/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Descuento actualizado exitosamente');
      } else {
        await axios.post('/api/custom-discounts', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Descuento creado exitosamente');
      }
      navigate('/discounts');
    } catch (error: any) {
      console.error('Error guardando descuento:', error);
      toast.error(isEditing ? 'Error actualizando descuento' : 'Error creando descuento');
    } finally {
      setLoading(false);
    }
  };

  const availableProducts = useMemo(
    () => productsList.filter((p) => !p.isService),
    [productsList]
  );
  const availableServices = useMemo(
    () => productsList.filter((p) => p.isService),
    [productsList]
  );
  const filteredProductOptions = useMemo(() => {
    const s = productSearch.trim().toLowerCase();
    if (!s) return availableProducts;
    return availableProducts.filter((p) => p.name.toLowerCase().includes(s));
  }, [availableProducts, productSearch]);
  const filteredServiceOptions = useMemo(() => {
    const s = serviceSearch.trim().toLowerCase();
    if (!s) return availableServices;
    return availableServices.filter((sv) => sv.name.toLowerCase().includes(s));
  }, [availableServices, serviceSearch]);
  const filteredClientOptions = useMemo(() => {
    const s = clientSearch.trim().toLowerCase();
    if (!s) return clientsList;
    return clientsList.filter((c) => {
      const name = c.type === 'company'
        ? c.companyName || ''
        : `${c.firstName || ''} ${c.lastName || ''}`.trim();
      return name.toLowerCase().includes(s);
    });
  }, [clientsList, clientSearch]);

  const formatClientName = (client: DiscountClient) => {
    return client.type === 'company'
      ? client.companyName || 'Empresa sin nombre'
      : `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Cliente sin nombre';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/discounts')} className="size-8">
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {isEditing ? 'Editar Descuento' : 'Nuevo Descuento'}
        </h1>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="discount-name" className="font-medium text-sm">Nombre *</Label>
              <Input
                id="discount-name"
                {...register('name', { required: 'El nombre es requerido' })}
                className="px-4 py-3 rounded-xl border border-input bg-background"
                placeholder="Descuento de temporada"
              />
              {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="discount-description" className="font-medium text-sm">Descripción</Label>
              <Textarea
                id="discount-description"
                {...register('description')}
                rows={3}
                className="px-4 py-3 rounded-xl border border-input bg-background resize-none"
                placeholder="Descripción del descuento..."
              />
            </div>

            {/* Tipo y valor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Tipo de Descuento *</Label>
                <Select
                  onValueChange={(v) => setValue('discount_type', v as 'percentage' | 'fixed')}
                  value={watch('discount_type')}
                >
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Monto Fijo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount-value" className="font-medium text-sm">Valor del Descuento *</Label>
                <Input
                  id="discount-value"
                  type="number"
                  step="0.01"
                  {...register('discount_value', {
                    required: 'El valor es requerido',
                    min: { value: 0.01, message: 'El valor debe ser mayor a 0' }
                  })}
                  className="px-4 py-3 rounded-xl border border-input bg-background"
                  placeholder="20"
                />
                {errors.discount_value && <p className="text-sm text-red-600 mt-1">{errors.discount_value.message}</p>}
              </div>
            </div>

            {/* Monto mínimo y descuento máximo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Monto Mínimo ($)</Label>
                <Input type="number" step="0.01" {...register('min_amount')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="0" />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Descuento Máximo ($)</Label>
                <Input type="number" step="0.01" {...register('max_discount')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="Sin límite" />
              </div>
            </div>

            {/* Alcance (audiencia) y Aplicar a (target) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Alcance *</Label>
                <Select
                  onValueChange={(v) => {
                    const next = v as 'all' | 'specific_clients';
                    setAudienceType(next);
                    if (next === 'all') setValue('eligible_clients', []);
                  }}
                  value={audienceType}
                >
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los clientes</SelectItem>
                    <SelectItem value="specific_clients">Cliente especifico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Aplicar a *</Label>
                <Select
                  onValueChange={(v) => {
                    const next = v as 'products' | 'services' | 'both';
                    setTargetType(next);
                    if (next === 'services') {
                      setValue('eligible_products', []);
                      setValue('filter_brand', '');
                      setValue('filter_model', '');
                      setValue('filter_category', '');
                      setValue('filter_supplier_name', '');
                      setValue('filter_measure_width', '');
                      setValue('filter_measure_ratio', '');
                      setValue('filter_rim', '');
                    }
                    if (next === 'products') {
                      setValue('eligible_services', []);
                      setValue('excluded_services', []);
                    }
                    if (next !== 'both') {
                      setSplitDiscount(false);
                      setValue('discount_value_services', 0);
                    }
                  }}
                  value={targetType}
                >
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="products">Productos</SelectItem>
                    <SelectItem value="services">Servicios</SelectItem>
                    <SelectItem value="both">Ambos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Cuando Alcance = Cliente especifico, mostrar lista de clientes */}
            {audienceType === 'specific_clients' && (
              <div className="space-y-2">
                <Label className="font-medium text-sm">Clientes habilitados</Label>
                <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Buscar cliente..." className="h-10 rounded-xl" />
                <div className="rounded-xl border border-input bg-background max-h-40 overflow-y-auto">
                  {filteredClientOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-4 py-3">Sin clientes disponibles</p>
                  ) : filteredClientOptions.map((client) => {
                    const selected = (watch('eligible_clients') || []).includes(client.id);
                    return (
                      <label key={client.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={(v) => {
                            const current = watch('eligible_clients') || [];
                            setValue('eligible_clients', v ? [...current, client.id] : current.filter((i) => i !== client.id));
                          }}
                        />
                        <span>{formatClientName(client)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cuando target=both, toggle de split % */}
            {targetType === 'both' && (
              <div className="space-y-2 rounded-xl border border-dashed border-input p-3">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={splitDiscount}
                    onCheckedChange={(v) => {
                      setSplitDiscount(!!v);
                      if (!v) setValue('discount_value_services', 0);
                    }}
                  />
                  <span>Usar % distinto para productos y servicios</span>
                </label>
                {splitDiscount && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <div className="space-y-1">
                      <Label className="text-xs">% Productos</Label>
                      <Input
                        type="number"
                        step="0.01"
                        {...register('discount_value', { required: true, min: 0 })}
                        className="h-9 rounded-lg"
                        placeholder="Ej: 5"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">% Servicios</Label>
                      <Input
                        type="number"
                        step="0.01"
                        {...register('discount_value_services')}
                        className="h-9 rounded-lg"
                        placeholder="Ej: 10"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {(targetType === 'products' || targetType === 'both') && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground">Productos</h4>
                {/* Sub-toggle: filtros vs lista especifica */}
                <div className="inline-flex rounded-xl border border-input p-1">
                  <button
                    type="button"
                    onClick={() => setProductMode('filters')}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-lg transition-colors',
                      productMode === 'filters' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                    )}
                  >
                    Por filtros
                  </button>
                  <button
                    type="button"
                    onClick={() => setProductMode('specific')}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-lg transition-colors',
                      productMode === 'specific' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                    )}
                  >
                    Lista especifica
                  </button>
                </div>

                {productMode === 'filters' && (
                  <div className="space-y-3 rounded-xl border border-dashed border-input p-4">
                    <p className="text-xs text-muted-foreground">
                      Filtros del descuento. Si dejas un campo vacio, no filtra por ese atributo.
                      Todos los campos llenos se combinan con AND.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Marca</Label>
                        <Select
                          value={watch('filter_brand') || '__all__'}
                          onValueChange={(v) => {
                            setValue('filter_brand', v === '__all__' ? '' : v);
                            setValue('filter_model', '');
                          }}
                        >
                          <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="Todas" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">Todas</SelectItem>
                            {meta.brands.map((b) => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Modelo</Label>
                        <Select
                          value={watch('filter_model') || '__all__'}
                          onValueChange={(v) => setValue('filter_model', v === '__all__' ? '' : v)}
                          disabled={!watch('filter_brand')}
                        >
                          <SelectTrigger className="h-9 rounded-lg">
                            <SelectValue placeholder={watch('filter_brand') ? 'Todos' : 'Elegi marca primero'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">Todos</SelectItem>
                            {(meta.modelsByBrand[String(watch('filter_brand') || '').toUpperCase()] || []).map((m) => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Categoria</Label>
                        <Select
                          value={watch('filter_category') || '__all__'}
                          onValueChange={(v) => {
                            setValue('filter_category', v === '__all__' ? '' : v);
                            // Si la categoria deja de ser neumaticos, limpiar medidas
                            if (v !== 'neumaticos') {
                              setValue('filter_measure_width', '');
                              setValue('filter_measure_ratio', '');
                              setValue('filter_rim', '');
                            }
                          }}
                        >
                          <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="Todas" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">Todas</SelectItem>
                            {meta.categories.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Medidas: solo cuando categoria=neumaticos */}
                      {String(watch('filter_category') || '').toLowerCase() === 'neumaticos' && (
                        <>
                          <div className="space-y-1">
                            <Label className="text-xs">Ancho</Label>
                            <Select
                              value={watch('filter_measure_width') || '__all__'}
                              onValueChange={(v) => setValue('filter_measure_width', v === '__all__' ? '' : v)}
                            >
                              <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__">Cualquiera</SelectItem>
                                {meta.measureWidths.map((w) => (
                                  <SelectItem key={w} value={String(w)}>{w}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Perfil</Label>
                            <Select
                              value={watch('filter_measure_ratio') || '__all__'}
                              onValueChange={(v) => setValue('filter_measure_ratio', v === '__all__' ? '' : v)}
                            >
                              <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__">Cualquiera</SelectItem>
                                {meta.measureRatios.map((r) => (
                                  <SelectItem key={r} value={String(r)}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Rodado</Label>
                            <Select
                              value={watch('filter_rim') || '__all__'}
                              onValueChange={(v) => setValue('filter_rim', v === '__all__' ? '' : v)}
                            >
                              <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__">Cualquiera</SelectItem>
                                {meta.rims.map((r) => (
                                  <SelectItem key={r} value={String(r)}>R{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      <div className="space-y-1 sm:col-span-3">
                        <Label className="text-xs">Proveedor vigente</Label>
                        <Select
                          value={watch('filter_supplier_name') || '__all__'}
                          onValueChange={(v) => setValue('filter_supplier_name', v === '__all__' ? '' : v)}
                        >
                          <SelectTrigger className="h-9 rounded-lg"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">Cualquiera</SelectItem>
                            {meta.suppliers.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {productMode === 'specific' && (
                  <div className="space-y-2">
                    <Label className="font-medium text-sm">Productos aplicables</Label>
                    <Input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Buscar producto..." className="h-10 rounded-xl" />
                    <div className="rounded-xl border border-input bg-background max-h-40 overflow-y-auto">
                      {filteredProductOptions.length === 0 ? (
                        <p className="text-sm text-muted-foreground px-4 py-3">Sin productos disponibles</p>
                      ) : filteredProductOptions.map((product) => {
                        const selected = (watch('eligible_products') || []).includes(product.id);
                        return (
                          <label key={product.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={(v) => {
                                const current = watch('eligible_products') || [];
                                setValue('eligible_products', v ? [...current, product.id] : current.filter((i) => i !== product.id));
                              }}
                            />
                            <span>{product.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(targetType === 'services' || targetType === 'both') && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground">Servicios</h4>
                <div className="inline-flex rounded-xl border border-input p-1">
                  <button
                    type="button"
                    onClick={() => setServiceMode('all')}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-lg transition-colors',
                      serviceMode === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                    )}
                  >
                    Todos los servicios
                  </button>
                  <button
                    type="button"
                    onClick={() => setServiceMode('specific')}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-lg transition-colors',
                      serviceMode === 'specific' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                    )}
                  >
                    Lista especifica
                  </button>
                </div>

                {serviceMode === 'all' && (() => {
                  // "Todos menos estos": los servicios nuevos entran solos
                  const excluidos: string[] = watch('excluded_services') || [];
                  const setExcluidos = (next: string[]) => setValue('excluded_services', next);
                  return (
                    <div className="space-y-2">
                      <div>
                        <Label className="font-medium text-sm">Excepto (opcional)</Label>
                        <p className="text-xs text-muted-foreground">
                          Entran todos los servicios, también los que cargues más adelante. Tildá los que no llevan este descuento.
                        </p>
                      </div>
                      {excluidos.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {excluidos.map((n) => (
                            <span key={n} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs">
                              Sin {n}
                              <button
                                type="button"
                                aria-label={`Volver a incluir ${n}`}
                                onClick={() => setExcluidos(excluidos.filter((x) => x !== n))}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <XMarkIcon className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <Input value={serviceSearch} onChange={(e) => setServiceSearch(e.target.value)} placeholder="Buscar servicio para excluir..." className="h-10 rounded-xl" />
                      <div className="rounded-xl border border-input bg-background max-h-40 overflow-y-auto">
                        {filteredServiceOptions.length === 0 ? (
                          <p className="text-sm text-muted-foreground px-4 py-3">Sin servicios disponibles</p>
                        ) : filteredServiceOptions.map((service) => {
                          const excluido = excluidos.includes(service.name);
                          return (
                            <label key={service.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                              <Checkbox
                                checked={excluido}
                                onCheckedChange={(v) => setExcluidos(v ? [...excluidos, service.name] : excluidos.filter((i) => i !== service.name))}
                              />
                              <span className={cn(excluido && 'line-through text-muted-foreground')}>{service.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {serviceMode === 'specific' && (
                  <div className="space-y-2">
                    <Input value={serviceSearch} onChange={(e) => setServiceSearch(e.target.value)} placeholder="Buscar servicio..." className="h-10 rounded-xl" />
                    <div className="rounded-xl border border-input bg-background max-h-40 overflow-y-auto">
                      {filteredServiceOptions.length === 0 ? (
                        <p className="text-sm text-muted-foreground px-4 py-3">Sin servicios disponibles</p>
                      ) : filteredServiceOptions.map((service) => {
                        const selected = (watch('eligible_services') || []).includes(service.name);
                        return (
                          <label key={service.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={(v) => {
                                const current = watch('eligible_services') || [];
                                setValue('eligible_services', v ? [...current, service.name] : current.filter((i) => i !== service.name));
                              }}
                            />
                            <span>{service.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Motivo y Uso */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Motivo</Label>
                <Select onValueChange={(v) => setValue('reason', v as CustomDiscountForm['reason'])} value={watch('reason') || ''}>
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fidelizacion">Fidelización</SelectItem>
                    <SelectItem value="reclamo">Reclamo</SelectItem>
                    <SelectItem value="promo">Promo</SelectItem>
                    <SelectItem value="cierre_venta">Cierre de venta</SelectItem>
                    <SelectItem value="error_interno">Error interno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Uso</Label>
                <Select onValueChange={(v) => setValue('usage_type', v as CustomDiscountForm['usage_type'])} value={watch('usage_type') || 'multiple'}>
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Uso único</SelectItem>
                    <SelectItem value="multiple">Uso múltiple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Fechas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Fecha de Inicio</Label>
                <DatePicker value={watch('start_date')} onChange={(d) => setValue('start_date', d)} />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Fecha de Expiración</Label>
                <DatePicker value={watch('end_date')} onChange={(d) => setValue('end_date', d)} />
              </div>
            </div>

            {/* Límites y prioridad */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Máximo de Usos</Label>
                <Input type="number" {...register('max_uses')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="0 = sin límite" />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Prioridad</Label>
                <Input type="number" {...register('priority')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="0" />
              </div>
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/discounts')} className="h-10 rounded-lg px-6">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="h-10 rounded-lg px-6 bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
              >
                {loading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear Descuento'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateDiscount;
